import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { CareEnvironment } from '@shared/appModel';
import { growthFactor } from '@shared/calculations/funding/projection/sharedPolicyConfig';
import {
  DAYS_IN_MONTH,
  unlimitedMaxBenefitMagicNumber,
} from '@shared/calculations/funding/projection/sharedConstants';
import { BalanceBreakdownTooltip } from '../BalanceBreakdownTooltip';
import { InflationTooltipContent } from '../cells';
import { currency } from '../formatters';
import { PROJECTED_COST_NOTE } from '../columnGroups';
import { GrowthFactor, HeaderTooltip } from '../HeaderTooltip';
import { AccumulationWhenCell, GrowthValueCell } from '../../accumulationTimeline/cells';
import type { AccumulationColumnSpec, AccumulationCtx } from '../../accumulationTimeline/types';
import { indemnityPayoutWaterfall } from './indemnityPayout';
import type { StrategyPresentation } from './types';

// ---------------------------------------------------------------------------
// Hybrid Life IRA — OneAmerica Asset Care III / Indexed Annuity Care.
//
// Two-pool reimbursement with a flat Face Amount death benefit, mirroring the
// Asset Care presentation conventions (AOB/COB framing, DB depletes on base
// drawdown only). The single divergence: the BASE monthly cap is a
// withdrawal-rate cap (inflatedFace × monthlyWithdrawalRate), NOT a fixed daily
// benefit — so the cell/tooltip read the strategy's precomputed
// `nominalDailyBenefitBase` (the at-issue withdrawal-rate daily) and the
// engine's `appliedMonthlyBenefitCap`, never re-implementing the
// withdrawal-rate math (constraint 9). The COB cap is the inflated
// per-environment daily benefit when one is quoted; with no quoted daily it
// falls back to the withdrawal-rate cap (these types are flagged
// hasUnverifiedModeling). A missing Continuation Benefit Amount means
// UNLIMITED COB: the engine seeds the sentinel and sets `terms.isUnlimited`,
// so COB-pool surfaces here render "Unlimited" instead of the raw ~$1B.
// ---------------------------------------------------------------------------

const hasAny = (timeline: AccumulationYear[], pick: (row: AccumulationYear) => number): boolean =>
  timeline.some(row => pick(row) > 0.5);

// Header-formula growth expression matching the engine's branch: compound
// renders the (1+r)^n form; simple riders (P2-38) render the linear form.
function growthExpression(isSimple: boolean, prefix: string, rateLabel: string) {
  if (isSimple) {
    return (
      <>
        {prefix} × (1 + {rateLabel} × min(policy year − 1, cap years))
      </>
    );
  }
  return (
    <>
      {prefix} ×{' '}
      <GrowthFactor rate={rateLabel} exponent="min(policy year − 1, cap years)" />
    </>
  );
}

const basePoolValue = (row: AccumulationYear): number =>
  Math.max(row.snapshot.theoreticalLtcPoolCeiling - row.snapshot.effectiveContinuationBenefit, 0);

function hybridLifeIraInflationTooltip({
  header,
  startingLabel,
  startingValue,
  rate,
  capYears,
  policyYear,
  result,
  compoundLabel,
  isSimple = false,
}: {
  header: string;
  startingLabel: string;
  startingValue: number;
  rate: number;
  capYears: number | undefined;
  policyYear: number;
  result: number;
  compoundLabel: string;
  // Simple riders grow linearly (P2-38) — the formula must narrate the form
  // the engine actually applied.
  isSimple?: boolean;
}) {
  const cap = capYears && capYears > 0 ? capYears : null;
  const intYear = Math.floor(policyYear);
  const rawIncrements = Math.max(intYear - 1, 0);
  const years = cap !== null ? Math.min(rawIncrements, cap) : rawIncrements;
  const capped = cap !== null && rawIncrements >= cap;

  return (
    <InflationTooltipContent
      header={header}
      startingLabel={startingLabel}
      startingValue={startingValue}
      rate={rate}
      years={years}
      result={result}
      note={capped ? `Growth stops after ${cap} annual increases` : undefined}
      compoundLabel={isSimple ? compoundLabel.replace('growth', 'simple growth') : compoundLabel}
      isSimple={isSimple}
    />
  );
}

function HybridLifeIraStaticValueTooltip({
  header,
  startingLabel,
  startingValue,
  result,
  driver,
}: {
  header: string;
  startingLabel: string;
  startingValue: number;
  result: number;
  driver: string;
}) {
  return (
    <TooltipContent className="text-xs">
      <div className="mb-1 font-semibold">{header}</div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">{startingLabel}</span>
        <span className="font-semibold tabular-nums">{currency(startingValue)}</span>
        <span className="text-gray-300">This policy year</span>
        <span className="font-semibold tabular-nums">{currency(result)}</span>
        <span className="text-gray-300">Driver</span>
        <span className="font-semibold">{driver}</span>
      </div>
    </TooltipContent>
  );
}

function buildHybridLifeIraAccumulationColumns({
  terms,
  timeline,
}: AccumulationCtx): AccumulationColumnSpec[] {
  const columns: AccumulationColumnSpec[] = [
    {
      key: 'when',
      label: 'Policy year',
      sticky: true,
      render: row => <AccumulationWhenCell row={row} />,
    },
  ];

  const firstRow = timeline[0];
  const baseAtIssue = terms.baseBenefitPoolAtIssue ?? (firstRow ? basePoolValue(firstRow) : 0);
  const cobAtIssue =
    terms.extensionOfBenefitsPoolAtIssue ?? firstRow?.snapshot.effectiveContinuationBenefit ?? 0;
  const baseRate = terms.baseBenefitPoolInflationRate ?? terms.inflationRate;
  const cobRate = terms.extensionOfBenefitsPoolInflationRate ?? terms.inflationRate;
  const baseCapYears = terms.baseBenefitPoolInflationCapYears ?? terms.inflationCapYears;
  const cobCapYears = terms.extensionOfBenefitsPoolInflationCapYears ?? terms.inflationCapYears;
  const baseMonthly = terms.nominalDailyBenefitBase * DAYS_IN_MONTH;

  if (hasAny(timeline, basePoolValue)) {
    columns.push({
      key: 'base-pool',
      label: 'Base pool',
      tooltip: (
        <HeaderTooltip
          description="Base long-term-care dollars available before benefits are used. This is the Face Amount / Acceleration of Benefits (AOB) pool, grown by the Face Amount inflation rate."
          formulas={[
            {
              label: 'Base pool',
              expression: growthExpression(
                terms.inflationIsSimple === true,
                'Face Amount',
                'Face Amount rate',
              ),
            },
          ]}
          notes="If a separate Face Amount rate is not listed, the policy-wide LTC inflation rate drives this value."
        />
      ),
      render: (row, prev) => {
        const value = basePoolValue(row);
        return (
          <GrowthValueCell
            value={value}
            prevValue={prev ? basePoolValue(prev) : null}
            tooltip={hybridLifeIraInflationTooltip({
              header: `Policy year ${row.policyYear} · base pool`,
              startingLabel: 'Face Amount at issue',
              startingValue: baseAtIssue,
              rate: baseRate,
              capYears: baseCapYears,
              policyYear: row.policyYear,
              result: value,
              compoundLabel: 'Face Amount anniversary growth',
              isSimple: terms.inflationIsSimple === true,
            })}
          />
        );
      },
    });
  }

  if (hasAny(timeline, row => row.snapshot.effectiveContinuationBenefit)) {
    // Unlimited COB: the pool holds the sentinel and never grows or depletes —
    // a static "Unlimited" beats a raw ~$1B figure with a growth tooltip.
    const cobUnlimited = cobAtIssue >= unlimitedMaxBenefitMagicNumber;
    columns.push({
      key: 'cob-pool',
      label: 'COB pool',
      tooltip: (
        <HeaderTooltip
          description="Continuation of Benefits (COB) dollars available after the base/AOB pool is used. Starts from the Continuation Benefit Amount and grows by the COB inflation rate."
          formulas={[
            {
              label: 'COB pool',
              expression: growthExpression(
                terms.inflationIsSimple === true,
                'Continuation Benefit Amount',
                'COB rate',
              ),
            },
          ]}
          notes="If a separate COB rate is not listed, the policy-wide LTC inflation rate drives this value. A policy with no Continuation Benefit Amount has unlimited COB."
        />
      ),
      render: (row, prev) =>
        cobUnlimited ? (
          <div className="font-medium text-gray-900">Unlimited</div>
        ) : (
          <GrowthValueCell
            value={row.snapshot.effectiveContinuationBenefit}
            prevValue={prev?.snapshot.effectiveContinuationBenefit ?? null}
            tooltip={hybridLifeIraInflationTooltip({
              header: `Policy year ${row.policyYear} · COB pool`,
              startingLabel: 'Continuation Benefit Amount at issue',
              startingValue: cobAtIssue,
              rate: cobRate,
              capYears: cobCapYears,
              policyYear: row.policyYear,
              result: row.snapshot.effectiveContinuationBenefit,
              compoundLabel: 'COB anniversary growth',
            })}
          />
        ),
    });
  }

  if (hasAny(timeline, row => row.snapshot.monthlyBenefitCap)) {
    columns.push({
      key: 'monthly-benefit',
      label: 'Monthly benefit',
      tooltip: (
        <HeaderTooltip
          description="Monthly LTC maximum before care begins. For Asset Care III, this is the grown Face Amount multiplied by the policy's monthly withdrawal rate."
          formulas={[
            {
              label: 'Monthly benefit',
              expression: growthExpression(
                terms.inflationIsSimple === true,
                'Face Amount × monthly withdrawal rate',
                'Face Amount rate',
              ),
            },
          ]}
          notes="The daily amount shown in the table is this monthly value divided by 30."
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.monthlyBenefitCap}
          prevValue={prev?.snapshot.monthlyBenefitCap ?? null}
          tooltip={hybridLifeIraInflationTooltip({
            header: `Policy year ${row.policyYear} · monthly benefit`,
            startingLabel: 'Face Amount at issue × monthly withdrawal rate',
            startingValue: baseMonthly,
            rate: baseRate,
            capYears: baseCapYears,
            policyYear: row.policyYear,
            result: row.snapshot.monthlyBenefitCap,
            compoundLabel: 'Face Amount anniversary growth',
            isSimple: terms.inflationIsSimple === true,
          })}
        />
      ),
    });
  }

  if (hasAny(timeline, row => row.snapshot.effectiveFaceAmount)) {
    columns.push({
      key: 'death-benefit',
      label: 'Death benefit',
      tooltip: (
        <HeaderTooltip
          description="Life-insurance death benefit before LTC benefits are used. Asset Care III keeps this at the Face Amount before care begins; LTC pool inflation does not increase it."
          formulas={[{ label: 'Death benefit', expression: 'Face Amount before LTC use' }]}
          notes="During care, base/AOB payments reduce the death benefit dollar for dollar; COB payments do not."
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.effectiveFaceAmount}
          prevValue={prev?.snapshot.effectiveFaceAmount ?? null}
          tooltip={
            <HybridLifeIraStaticValueTooltip
              header={`Policy year ${row.policyYear} · death benefit`}
              startingLabel="Face Amount"
              startingValue={row.snapshot.effectiveFaceAmount}
              result={row.snapshot.effectiveFaceAmount}
              driver="Face Amount; no pre-care inflation; base/AOB use later reduces it, COB use does not"
            />
          }
        />
      ),
    });
  }

  return columns;
}

export const hybridLifeIraPresentation: StrategyPresentation = {
  accumulationColumns: buildHybridLifeIraAccumulationColumns,

  benefitAmountCell: ({ month, terms }) => {
    if (month.events.ineligibilityReason === 'pre-policy') return null;
    const monthlyCap = month.facts.appliedMonthlyBenefitCap;
    const fromEob = month.facts.appliedCapPoolSource === 'eob';
    // Base derivation starts from the at-issue withdrawal-rate daily; COB
    // derivation starts from the per-environment COB daily benefit.
    const dailyByEnvironment = terms.nominalDailyBenefitByEnvironment ?? {};
    const startingDaily = fromEob
      ? (dailyByEnvironment[month.context.careEnvironment] ?? 0)
      : terms.nominalDailyBenefitBase;
    const startingMonthly = startingDaily * DAYS_IN_MONTH;
    const rate = fromEob
      ? (terms.extensionOfBenefitsPoolInflationRate ?? terms.inflationRate)
      : (terms.baseBenefitPoolInflationRate ?? terms.inflationRate);
    const capYears = fromEob
      ? (terms.extensionOfBenefitsPoolInflationCapYears ?? terms.inflationCapYears)
      : (terms.baseBenefitPoolInflationCapYears ?? terms.inflationCapYears);
    const intYear = Math.floor(month.context.policyYear);
    const cap = capYears && capYears > 0 ? capYears : null;
    const rawIncrements = Math.max(intYear - 1, 0);
    const increments = cap !== null ? Math.min(rawIncrements, cap) : rawIncrements;
    const capped = cap !== null && intYear > cap;
    const poolLabel = fromEob ? 'COB' : 'Withdrawal-rate base';
    const startingLabel = fromEob ? 'At issue (COB daily)' : 'At issue (base daily)';
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help leading-tight">
            <div className="font-medium text-gray-900">{currency(monthlyCap)}</div>
            <div className="text-[10px] text-gray-500">
              {currency(monthlyCap / DAYS_IN_MONTH)}/day
            </div>
          </div>
        </TooltipTrigger>
        <InflationTooltipContent
          header={`Policy year ${intYear} · ${poolLabel} monthly limit`}
          startingLabel={startingLabel}
          startingValue={startingMonthly}
          rate={rate}
          years={increments}
          result={monthlyCap}
          note={capped ? `${poolLabel} inflation capped at year ${cap}` : undefined}
          compoundLabel={terms.inflationIsSimple === true ? 'Simple growth' : 'Compound growth'}
          isSimple={terms.inflationIsSimple === true}
        />
      </Tooltip>
    );
  },

  benefitAmountValueForMonth: ({ month }) =>
    month.events.ineligibilityReason === 'pre-policy'
      ? undefined
      : month.facts.appliedMonthlyBenefitCap,

  poolCellTooltip: ({ month, openState, terms }) => {
    const openBase = openState.pools.baseBenefitPool;
    const openEob = openState.pools.extensionOfBenefitsPool;
    const closeBase = month.state.pools.baseBenefitPool;
    const closeEob = month.state.pools.extensionOfBenefitsPool;
    const baseDepletion = month.deltas.base.depletion;
    const eobDepletion = month.deltas.eob.depletion;
    const baseAccrual = month.deltas.base.inflationAccrual;
    const eobAccrual = month.deltas.eob.inflationAccrual;
    // Unlimited COB: the running COB balance is the sentinel minus draws — not
    // a meaningful figure, so show the base breakdown and explain the COB.
    const cobUnlimited = terms.isUnlimited === true;

    const allZero =
      openBase === 0 &&
      openEob === 0 &&
      closeBase === 0 &&
      closeEob === 0 &&
      baseDepletion === 0 &&
      eobDepletion === 0 &&
      baseAccrual === 0 &&
      eobAccrual === 0;
    if (allZero) return null;

    return (
      <BalanceBreakdownTooltip
        title="LTC Pool Breakdown"
        description="Hybrid Life IRA pays from the Face / Acceleration of Benefits pool first, then Continuation of Benefits (COB). Inflation accrues at policy anniversaries while each pool remains open."
        sections={[
          {
            heading: 'Face / Acceleration of Benefits (base)',
            opening: openBase,
            closing: closeBase,
            lines: [
              { label: 'Inflation accrual', value: baseAccrual, kind: 'add' },
              { label: 'Drawn this month', value: baseDepletion, kind: 'subtract' },
            ],
            note: cobUnlimited
              ? `Continuation of Benefits (COB) is unlimited — it never depletes.${
                  eobDepletion > 0 ? ` COB paid ${currency(eobDepletion)} this month.` : ''
                }`
              : undefined,
          },
          ...(cobUnlimited
            ? []
            : [
                {
                  heading: 'Continuation of Benefits (COB)',
                  opening: openEob,
                  closing: closeEob,
                  lines: [
                    { label: 'Inflation accrual', value: eobAccrual, kind: 'add' as const },
                    { label: 'Drawn this month', value: eobDepletion, kind: 'subtract' as const },
                  ],
                },
              ]),
        ]}
      />
    );
  },

  deathBenefitCellTooltip: ({ month, openState }) => {
    const open = openState.pools.deathBenefit;
    const close = month.state.pools.deathBenefit;
    const reduction = month.deltas.deathBenefit.reduction;
    const flooredAtMinimum = month.deltas.deathBenefit.flooredAtMinimum;
    const baseDrawdown = month.deltas.base.depletion;

    if (open === 0 && close === 0 && reduction === 0) return null;

    const floorNote = flooredAtMinimum
      ? `Base drew ${currency(baseDrawdown)}; death benefit reduction capped at minimum guaranteed ${currency(close)}.`
      : undefined;

    return (
      <BalanceBreakdownTooltip
        title="Death benefit"
        description="For Hybrid Life IRA, only base-pool (Face / AOB) drawdown reduces the death benefit. COB drawdown does not reduce the death benefit."
        sections={[
          {
            opening: open,
            closing: close,
            lines: [{ label: 'Reduced this month', value: reduction, kind: 'subtract' }],
            note: floorNote,
          },
        ]}
      />
    );
  },

  payoutCellTooltip: ({ month, terms }) => {
    const eligibleDays = month.facts.coverageEligibleDays;
    if (eligibleDays === 0) return null;

    const cost = month.facts.effectiveCareCost;
    const dailyCost = month.facts.dailyCareCost;
    const paid = month.deltas.payout.ltcCoveragePaid;
    const drawnBase = month.deltas.base.depletion;
    const drawnEob = month.deltas.eob.depletion;
    const isPartial = eligibleDays < DAYS_IN_MONTH;
    const eligibleCost = isPartial ? eligibleDays * dailyCost : cost;
    const oopOverCap = Math.max(eligibleCost - paid, 0);
    const breakdown = month.events.ineligibleDayBreakdown;
    const oopLt2Adl = breakdown.lt2AdlDays * dailyCost;
    const oopWp = breakdown.inWpDays * dailyCost;
    const oopIl = breakdown.ilNotCoveredDays * dailyCost;
    const oopPrePolicy = breakdown.prePolicyDays * dailyCost;
    const totalOop = oopOverCap + oopLt2Adl + oopWp + oopIl + oopPrePolicy;

    // When a month draws from BOTH pools the base was exhausted mid-month.
    // Reconstruct per-pool day counts from the SAME precomputed daily rates the
    // strategy used: base daily = at-issue withdrawal-rate daily × base factor;
    // COB daily = per-environment daily × COB factor. Indemnity leaves these
    // uncapped; reimbursement caps at the daily cost.
    const bothPoolsPaid = drawnBase > 0 && drawnEob > 0;
    const dailyByEnvironment = terms.nominalDailyBenefitByEnvironment ?? {};
    const policyYear = month.context.policyYear;
    const baseRate = terms.baseBenefitPoolInflationRate ?? terms.inflationRate;
    const eobRate = terms.extensionOfBenefitsPoolInflationRate ?? terms.inflationRate;
    const baseCapYears = terms.baseBenefitPoolInflationCapYears ?? terms.inflationCapYears;
    const eobCapYears = terms.extensionOfBenefitsPoolInflationCapYears ?? terms.inflationCapYears;
    const isSimple = terms.inflationIsSimple === true;
    const baseDailyCap =
      terms.nominalDailyBenefitBase *
      growthFactor(baseRate, baseCapYears ?? 0, isSimple, policyYear);
    const cobAtIssueDaily = dailyByEnvironment[month.context.careEnvironment] ?? 0;
    const eobDailyCap =
      cobAtIssueDaily * growthFactor(eobRate, eobCapYears ?? 0, isSimple, policyYear);
    const basePerDay = terms.isIndemnity ? baseDailyCap : Math.min(dailyCost, baseDailyCap);
    const eobPerDay = terms.isIndemnity ? eobDailyCap : Math.min(dailyCost, eobDailyCap);
    const baseDaysDrawn = basePerDay > 0 ? drawnBase / basePerDay : 0;
    const cobDaysDrawn = eobPerDay > 0 ? drawnEob / eobPerDay : 0;

    const mode = terms.isIndemnity ? 'indemnity' : 'reimbursement';
    const modeText = terms.isIndemnity
      ? 'Pays the withdrawal-rate daily as cash (uncapped) for the base pool, then the COB daily, regardless of actual cost. Cash above the eligible cost banks as a surplus reserve, drained to cover cost on later shortfall months.'
      : 'Reimburses eligible incurred cost, up to the withdrawal-rate base cap then the COB cap.';

    return (
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Projected payout — {mode}</div>
        <div className="mb-1 text-[10px] text-gray-300">{modeText}</div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          {terms.isIndemnity ? (
            // Indemnity is a cash waterfall, not a day-bucket breakdown — see
            // `indemnityPayoutWaterfall`.
            indemnityPayoutWaterfall(month)
          ) : (
            <>
              <span className="text-gray-300">Care cost</span>
              <span className="font-semibold tabular-nums">{currency(cost)}</span>
              {isPartial && (
                <>
                  <span className="text-gray-300">
                    Eligible ({eligibleDays}/{DAYS_IN_MONTH} days)
                  </span>
                  <span className="font-semibold tabular-nums">{currency(eligibleCost)}</span>
                </>
              )}
              <span className="text-gray-300">Policy paid</span>
              <span className="font-semibold tabular-nums">−{currency(paid)}</span>
              <span className="text-gray-300">Out of pocket</span>
              <span className="font-semibold tabular-nums text-rose-300">{currency(totalOop)}</span>
              {oopOverCap > 0 && (
                <>
                  <span className="pl-2 text-gray-400">· over cap</span>
                  <span className="tabular-nums text-rose-300">{currency(oopOverCap)}</span>
                </>
              )}
              {oopWp > 0 && (
                <>
                  <span className="pl-2 text-gray-400">
                    · waiting period <span className="text-gray-500">· {breakdown.inWpDays}d</span>
                  </span>
                  <span className="tabular-nums text-rose-300">{currency(oopWp)}</span>
                </>
              )}
              {oopLt2Adl > 0 && (
                <>
                  <span className="pl-2 text-gray-400">
                    {'· <2 ADL'} <span className="text-gray-500">· {breakdown.lt2AdlDays}d</span>
                  </span>
                  <span className="tabular-nums text-rose-300">{currency(oopLt2Adl)}</span>
                </>
              )}
              {oopIl > 0 && (
                <>
                  <span className="pl-2 text-gray-400">
                    · independent living
                    <span className="text-gray-500"> · {breakdown.ilNotCoveredDays}d</span>
                  </span>
                  <span className="tabular-nums text-rose-300">{currency(oopIl)}</span>
                </>
              )}
              {oopPrePolicy > 0 && (
                <>
                  <span className="pl-2 text-gray-400">
                    · before policy start
                    <span className="text-gray-500"> · {breakdown.prePolicyDays}d</span>
                  </span>
                  <span className="tabular-nums text-rose-300">{currency(oopPrePolicy)}</span>
                </>
              )}
            </>
          )}
        </div>
        {bothPoolsPaid ? (
          <div className="mt-1 border-t border-gray-700 pt-1">
            <div className="mb-0.5 text-[10px] text-gray-400">
              Base pool exhausted this month — COB covers the rest:
            </div>
            <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
              <span className="text-gray-300">
                Base · {baseDaysDrawn.toFixed(1)}d × {currency(basePerDay)}/day
              </span>
              <span className="font-semibold tabular-nums">{currency(drawnBase)}</span>
              <span className="text-gray-300">
                COB · {cobDaysDrawn.toFixed(1)}d × {currency(eobPerDay)}/day
              </span>
              <span className="font-semibold tabular-nums">{currency(drawnEob)}</span>
            </div>
          </div>
        ) : drawnBase > 0 || drawnEob > 0 ? (
          <div className="mt-1 text-[10px] text-gray-400">
            Drawn from {drawnBase > 0 ? 'base (Face / AOB)' : 'COB'}{' '}
            {currency(drawnBase > 0 ? drawnBase : drawnEob)}
          </div>
        ) : null}
      </TooltipContent>
    );
  },

  paidCellExtras: month => {
    const isHomeBucket = month.context.careEnvironment === CareEnvironment.home;
    const wpConsumed = isHomeBucket
      ? month.facts.waitingPeriodDaysConsumedHome
      : month.facts.waitingPeriodDaysConsumedFacility;

    if (!(wpConsumed > 0 && month.deltas.payout.ltcCoveragePaid === 0)) return null;

    return (
      <div className="mt-0.5 text-[10px] text-amber-700">
        {`${isHomeBucket ? 'Home' : 'Facility'} WP serving ${wpConsumed}d`}
      </div>
    );
  },

  // The base monthly cap grows on the base (Face) inflation rate.
  benefitInflationRate: terms => terms.baseBenefitPoolInflationRate ?? terms.inflationRate,

  copy: {
    poolTooltip: (
      <HeaderTooltip
        description="End-of-month LTC pool balance. Hybrid Life IRA draws from the Face / Acceleration of Benefits pool first, then Continuation of Benefits (COB). Inflation can accrue at policy anniversaries while pools remain open. Modeling is unverified (no carrier illustration)."
        formulas={[
          {
            label: 'Closing pool',
            expression: '(opening base + opening COB) + inflation accrual − drawn this month',
          },
          { label: 'Draw order', expression: 'base first; COB engages once base is exhausted' },
        ]}
      />
    ),
    payoutColumnTooltip: (
      <HeaderTooltip
        description="Policy payout this month (reimbursement or indemnity), with cumulative dollars paid by the policy through this month in parentheses. Modeling is unverified — base payout is withdrawal-rate driven; COB pays the per-environment daily when quoted, otherwise it continues the withdrawal-rate benefit."
        formulas={[
          {
            label: 'Reimbursement mode',
            expression: 'min(eligible cost, withdrawal-rate base cap, then COB cap)',
          },
          {
            label: 'Indemnity mode',
            expression: 'withdrawal-rate base daily (uncapped) × eligible days, then COB daily',
          },
        ]}
        notes="Eligible cost excludes waiting-period and <2 ADL days. Indemnity does not apply an indemnity-percent multiplier."
      />
    ),
    costTooltip: (
      <HeaderTooltip
        description="Projected total monthly care cost (with inflated per-day rate beneath). The daily rate comes from the care plan's phase × environment unit cost."
        formulas={[
          {
            label: 'Daily cost',
            expression: (
              <>
                today's daily unit cost ×{' '}
                <GrowthFactor
                  rate="care-cost inflation"
                  exponent="years from today"
                />
              </>
            ),
          },
          { label: 'Monthly cost', expression: 'daily cost × days in month' },
        ]}
        notes={PROJECTED_COST_NOTE}
      />
    ),
    benefitAmountTooltip: (
      <HeaderTooltip
        description="Policy monthly benefit maximum for this policy year, with the daily equivalent beneath. The base cap is the withdrawal-rate cap (inflated Face × monthly withdrawal rate); once the base pool is exhausted the draw moves to COB at the COB daily cap. Modeling is unverified."
        formulas={[
          {
            label: 'Base phase',
            expression: (
              <>
                at-issue Face × monthly withdrawal rate ×{' '}
                <GrowthFactor
                  rate="base inflation rate"
                  exponent="policy year − 1"
                />
              </>
            ),
          },
          {
            label: 'COB phase',
            expression: (
              <>
                at-issue COB daily × days in month ×{' '}
                <GrowthFactor
                  rate="COB inflation rate"
                  exponent="policy year − 1"
                />
              </>
            ),
          },
        ]}
        notes="Each pool's inflation is capped at its configured cap year if specified. With no per-environment daily benefit configured, the COB phase continues the withdrawal-rate cap instead."
      />
    ),
    poolLimitTooltip: (
      <HeaderTooltip
        description="Inflated base (Face / AOB) + COB pool sizes at this policy year if no benefits were used. Pools keep growing on each future anniversary until any inflation cap is reached. Distinct from Pool remaining, which is the actual balance after payouts."
        formulas={[
          {
            label: 'Base',
            expression: (
              <>
                Face at issue ×{' '}
                <GrowthFactor
                  rate="base inflation rate"
                  exponent="policy year − 1"
                />
              </>
            ),
          },
          {
            label: 'COB',
            expression: (
              <>
                COB at issue ×{' '}
                <GrowthFactor
                  rate="COB inflation rate"
                  exponent="policy year − 1"
                />
              </>
            ),
          },
          { label: 'Total', expression: 'base value + COB value' },
        ]}
        notes="A 0% inflation rider on a pool leaves that pool flat. Once a pool's inflation cap is reached, its value is a true ceiling."
      />
    ),
    drawnTooltip: (
      <HeaderTooltip
        description="Cumulative reduction of the base + COB pools through this month — any utilization before the projection plus this projection's drawdown."
        formulas={[
          { label: 'Drawn', expression: 'prior utilization + Σ (base depletion + COB depletion)' },
          {
            label: 'Identity',
            expression: 'Pool limit − Drawn = Pool remaining (while pool is open)',
          },
        ]}
      />
    ),
    deathBenefitTooltip: (
      <HeaderTooltip
        description="End-of-month death benefit, with this month's change beneath. Hybrid Life IRA depletes the death benefit dollar-for-dollar by base-pool (Face / AOB) drawdown only; COB drawdown does not reduce the death benefit."
        formulas={[
          { label: 'Death benefit', expression: 'at-issue Face Amount − Σ (base drawdown)' },
          { label: 'Floor', expression: 'max(above, minimum guaranteed amount)' },
        ]}
      />
    ),
    benefitInflationCompoundLabel: 'Compound growth',
    benefitInflationCapNote: capYear => `Inflation capped at year ${capYear}`,
  },
};
